import { Button, Modal, Table } from 'react-dsgov';
import { IOperacao, IOperacaoListagem } from '../../../../../../../../interfaces/IOperacaoPLG';
import { BrButton, BrLoading } from '@govbr-ds/react-components';
import styles from './operacaoTable.module.scss';
import { formatCurrency } from '../../../../../../../../utils/formatCurrency';
import { useContext, useEffect, useState } from 'react';
import { formatarNrProcesso } from '../../../../../../../../utils/formatNrProcesso';
import { useBackEndRequests } from '../../../../../../lib/backEndRequests';
import { useQuery } from '@tanstack/react-query';
import { OperacaoDetalhe } from '../../../../../../../../enums/ETipoOperacao';
import { DiefCfemContext } from '../../../../../../../../contexts/DiefCfemContext';
import { listarErrosValidacao } from '../../../../../../../../utils/formatErrosValidacao';
import CustomConfirmer from '../../../../../../../../components/customConfirmer/customConfirmer';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IHeader {
  field: string;
  label: string;
}

interface OperacaoTableProps {
  idDeclaracao: string;
  headers: IHeader[];
  isLoading: boolean;
  setEdicaoOperacao: React.Dispatch<React.SetStateAction<IOperacao | undefined>>;
  setNovaOperacaoPLG: React.Dispatch<React.SetStateAction<boolean>>;
}

export const OperacaoTable = ({
  idDeclaracao,
  headers,
  isLoading,
  setEdicaoOperacao,
  setNovaOperacaoPLG,
}: OperacaoTableProps) => {
  const [idOperacaoADeletar, setIdOperacaoADeletar] = useState<number>();
  const [openModal, setOpenModal] = useState(false);
  const [exclusaoConfirmada, setExclusaoConfirmada] = useState(false);

  const { deleteOperacaoPLG, getDeclaracaoResumoPLGById } = useBackEndRequests();

  const { data: deleteOperacaoData } = useQuery<string, IErrorWithSentryId>({
    queryKey: ['deleteOperacaoPLG', idOperacaoADeletar],
    queryFn: async () => {
      await deleteOperacaoPLG(idOperacaoADeletar!);
      return 'Operação excluída com sucesso';
    },
    enabled: !!idOperacaoADeletar && exclusaoConfirmada,
  });

  const context = useContext(DiefCfemContext);
  const abrirFecharModal = () => {
    setOpenModal(!openModal);
  };

  useEffect(() => {
    if (deleteOperacaoData) {
      if (context) {
        context.declaracaoTitularArrendatario.operacoes = context.declaracaoTitularArrendatario.operacoes?.filter(
          (operacao) => operacao.id !== idOperacaoADeletar
        );
        context.setDeclaracaoTitularArrendatario({
          ...context.declaracaoTitularArrendatario,
        });
      }
      setExclusaoConfirmada(false);
      setIdOperacaoADeletar(undefined);
      refetch();
    }
    if (context?.errosValidacaoDeclaracao?.tabNContent && context.errosValidacaoDeclaracao.tabNContent.length > 0) {
      const validarOperacoes = async () => {
        if (!context?.declaracaoTitularArrendatario.id) {
          return;
        }
        await getDeclaracaoResumoPLGById(context.declaracaoTitularArrendatario.id)
          .then(() => {
            context?.setErrosValidacaoDeclaracao({ tabNContent: [], showContainer: false });
          })
          .catch((err) => {
            if (err.response.status === 422) {
              const tabNContent = listarErrosValidacao(err.response.data.detail);
              context?.setErrosValidacaoDeclaracao({
                tabNContent: tabNContent,
                showContainer: true,
              });
            }
          });
      };
      validarOperacoes();
    }
  }, [deleteOperacaoData]);

  const confirmarExclusao = async () => {
    setExclusaoConfirmada(true);
    abrirFecharModal();
  };

  const excluirOperacao = (idDeclaracaoItem: number) => {
    abrirFecharModal();
    setIdOperacaoADeletar(idDeclaracaoItem);
  };

  const editarOperacao = (operacao: IOperacao) => {
    setEdicaoOperacao(operacao);
    setNovaOperacaoPLG(true);
    window.scrollTo(0, 0);
  };

  const { getOperacoesByDeclaracaoId } = useBackEndRequests();

  const {
    data: operacaoListagemData,
    isLoading: isOperacaoListagemDataLoading,
    refetch,
  } = useQuery<IOperacaoListagem, IErrorWithSentryId>({
    queryKey: ['declaracaoPrimeiroAdquirenteQuery', [idDeclaracao]],
    queryFn: () => getOperacoesByDeclaracaoId(idDeclaracao || ''),
    refetchOnWindowFocus: true,
    retryDelay: 100,
  });

  useEffect(() => {
    if (operacaoListagemData) {
      const operacoes: IOperacao[] = [];
      operacaoListagemData.forEach((operacao: IOperacaoListagem) => {
        operacao.operacoes.forEach((item: IOperacao) => {
          operacoes.push(item);
        });
      });
      if (context?.declaracaoTitularArrendatario) {
        const declaracaoAtualizada = {
          ...context.declaracaoTitularArrendatario,
          operacoes: operacoes,
        };
        context.setDeclaracaoTitularArrendatario(declaracaoAtualizada);
      }
    }
  }, [operacaoListagemData]);

  return (
    <div className={styles.customTable}>
      <Modal title='Excluir Operação PLG' useScrim modalOpened={openModal}>
        <Modal.Body>Deseja excluir a operação?</Modal.Body>
        <Modal.Footer justify-content='end'>
          <Button secondary small m={2} onClick={abrirFecharModal}>
            Cancelar
          </Button>
          <CustomConfirmer asyncCall={() => confirmarExclusao()}>Confirmar</CustomConfirmer>
        </Modal.Footer>
      </Modal>
      {(operacaoListagemData && operacaoListagemData.length === 0) ||
      (!operacaoListagemData && !isOperacaoListagemDataLoading) ? (
        <Table
          title={'Processo Minerário - Nº (-) | Título - Lavra Garimpeira'}
          showTopBar={true}
          headers={headers}
          showPagination={false}
          showDensityButtons={false}
          showSearch={false}
          density='medium'
        >
          <tbody className={styles.noRecords}>
            <td className={styles.noRecords} colSpan={headers.length}>
              Não há operações para serem exibidas. <br />
              Por favor adicione uma operação.
            </td>
          </tbody>
        </Table>
      ) : (
        operacaoListagemData?.map((row: IOperacaoListagem, index: number) => (
          <div>
            <Table
              title={
                'Processo Minerário - Nº ' +
                (formatarNrProcesso(row.numeroProcesso) || '-') +
                ' | ' +
                (row.titulo || 'Título - Lavra Garimpeira')
              }
              showTopBar={true}
              headers={headers}
              showPagination={false}
              showDensityButtons={false}
              showSearch={false}
              density='medium'
              className={styles.operacaoTable}
              key={index}
            >
              <tbody>
                {Array.isArray(row.operacoes) &&
                  row.operacoes.map((rowOperacao, index) => (
                    <tr key={rowOperacao.id}>
                      {headers.map((header) => {
                        if (header?.label === 'Ações')
                          return (
                            <>
                              <td align='center' key={header.field}>
                                <div className={styles.statusColumn}>
                                  <BrButton secondary circle onClick={() => editarOperacao(rowOperacao)} title='Editar'>
                                    <i className={`fas fa-edit fa-md  ${styles.columnIcon}`} />
                                  </BrButton>
                                  <BrButton
                                    secondary
                                    circle
                                    onClick={() => excluirOperacao(rowOperacao.id)}
                                    title='Excluir'
                                  >
                                    <i className={`fas fa-trash-alt fa-md  ${styles.columnIcon}`} />
                                  </BrButton>
                                </div>
                              </td>
                            </>
                          );
                        else if (header?.label === 'Preço Unitário') {
                          return (
                            <td align='center' key={header.field}>
                              {formatCurrency(
                                parseFloat(rowOperacao.valorAquisicao || '') /
                                  parseFloat(rowOperacao.quantidade?.toString() || '')
                              ) || '-'}
                            </td>
                          );
                        } else if (header?.field === 'valorTotal') {
                          return (
                            <td align='center' key={header.field}>
                              {formatCurrency(parseFloat(rowOperacao.valorTotal || '')) || '-'}
                            </td>
                          );
                        } else if (header?.label === 'Valor de Aquisição')
                          return (
                            <td align='center' key={header.field}>
                              {formatCurrency(rowOperacao[header.field]) || '-'}
                            </td>
                          );
                        else if (header?.label === 'Quantidade')
                          return (
                            <td align='center' key={header.field}>
                              {rowOperacao[header.field].toString().replace('.', ',') || '-'}
                            </td>
                          );
                        else if (header?.label === 'Município/UF') {
                          return (
                            <td align='center' key={header.field}>
                              <div>
                                {rowOperacao.municipio
                                  ? `${rowOperacao.municipio?.nome}/${rowOperacao?.municipio?.uf}`
                                  : 'teste'}
                              </div>
                            </td>
                          );
                        } else if (
                          //header?.label === 'Substância' ||
                          header?.label === 'Unidade' ||
                          header?.label === 'Tipo de Uso'
                        )
                          return (
                            <td align='center' key={header.field}>
                              {rowOperacao[header.field] || '-'}
                            </td>
                          );
                        else if (header?.field === 'houveFatoGerador')
                          return (
                            <td align='center' key={header.field}>
                              {rowOperacao[header.field] === true ? 'Sim' : 'Não'}
                            </td>
                          );
                        else if (header?.field === 'houveProducao')
                          return (
                            <td align='center' key={header.field}>
                              {rowOperacao[header.field] === true ? 'Sim' : 'Não'}
                            </td>
                          );
                        else if (header?.field === 'tipoOperacao')
                          return (
                            <td align='center' key={header.field}>
                              {OperacaoDetalhe[rowOperacao[header.field] as unknown as keyof typeof OperacaoDetalhe]
                                ?.label || '-'}
                            </td>
                          );
                        else
                          return (
                            <td align='center' key={header.field}>
                              {rowOperacao[header.field] || '-'}
                            </td>
                          );
                      })}
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        ))
      )}
      {(isLoading || isOperacaoListagemDataLoading) && (
        <div className={styles.tableLoading}>
          <BrLoading large label='Carregando...' />
        </div>
      )}
    </div>
  );
};
